import { createPortal } from 'react-dom'
import { Component, lazy, Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowDown, Maximize2, MessageCircle, Minimize2, Send, Smile, SmilePlus, X } from 'lucide-react'
import { useTasks } from '../TaskProvider'
import { PresenceDot, useLiveRoom } from '../LiveRoom'
import { roomRequest } from '../roomApi'
import { useRouteState } from '../routeState'
import { readBrowserValue, writeBrowserValue } from '../browserStorage'
import { useRefreshGuard } from '../refreshGuards'
import type { ChatHistory, ChatMessage, ChatSummary, RoomMember } from '../types'

const ChatEmojiPicker = lazy(() => import('./ChatEmojiPicker'))

class EmojiPickerBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() { return { failed: true } }
  render() {
    return this.state.failed ? <p className="chat-empty" role="alert">Emoji couldn’t load. You can keep writing. Reconnect and refresh Bramble to open the picker.</p> : this.props.children
  }
}

export function ChatWidget({ host }: { host: Element | null }) {
  const { user, members } = useTasks(), live = useLiveRoom()
  const [partnerId, setPartnerId] = useRouteState<string>('chat', '')
  const [expanded, setExpanded] = useRouteState<boolean>('chat-expanded', false)
  const [summary, setSummary] = useState<ChatSummary>({ unreadCount: 0, threads: [] })
  const dismissalKey = 'bramble:chat-preview-dismissed:' + user.id
  const [error, setError] = useState(''), [dismissed, setDismissedValue] = useState(() => Number(readBrowserValue(dismissalKey)) || 0)
  const setDismissed = useCallback((sequence: number) => setDismissedValue(previous => {
    const next = Math.max(previous, sequence)
    writeBrowserValue(dismissalKey, String(next)); return next
  }), [dismissalKey])
  const [viewport, setViewport] = useState({ height: window.innerHeight, bottom: 0, mobile: window.innerWidth <= 620 })
  const dock = useRef<HTMLElement>(null)
  useEffect(() => {
    const visual = window.visualViewport
    const update = () => setViewport({ height: visual?.height || window.innerHeight, bottom: visual ? Math.max(0, window.innerHeight - visual.height - visual.offsetTop) : 0, mobile: window.innerWidth <= 620 })
    update(); visual?.addEventListener('resize', update); visual?.addEventListener('scroll', update); window.addEventListener('resize', update)
    return () => { visual?.removeEventListener('resize', update); visual?.removeEventListener('scroll', update); window.removeEventListener('resize', update) }
  }, [])
  const version = useRef(0)
  const refresh = useCallback(async () => {
    const request = ++version.current
    try {
      const data = await roomRequest<ChatSummary>(user.id, '/api/chat')
      if (request === version.current) { setSummary(data); setError('') }
    } catch (e) { if (request === version.current) setError(e instanceof Error ? e.message : 'Messages are unavailable.') }
  }, [user.id])
  useEffect(() => {
    void refresh()
    const visible = () => { if (document.visibilityState === 'visible') void refresh() }
    const timer = window.setInterval(visible, 20000)
    window.addEventListener('bramble:chat-changed', visible); window.addEventListener('focus', visible)
    document.addEventListener('visibilitychange', visible)
    return () => { ++version.current; clearInterval(timer); window.removeEventListener('bramble:chat-changed', visible); window.removeEventListener('focus', visible); document.removeEventListener('visibilitychange', visible) }
  }, [refresh, live.connected])
  const others = members.filter(member => member.id !== user.id)
  const unread = summary.threads.filter(thread => thread.unreadCount).sort((a, b) => (b.lastMessage?.sequence || 0) - (a.lastMessage?.sequence || 0))[0]
  const suggested = unread?.userId || others[0]?.id
  const partner = others.find(member => member.id === partnerId)
  const open = (id = suggested) => { if (id) { setPartnerId(id); setExpanded(true) } }
  const latestUnread = unread?.lastMessage?.sequence || 0
  const preview = latestUnread > dismissed && !!unread && (!partner || (!expanded && viewport.mobile))
  const close = () => { setPartnerId(''); setExpanded(false); setDismissed(latestUnread) }
  useEffect(() => {
    if (!viewport.mobile || !preview) return
    const timer = window.setTimeout(() => setDismissed(latestUnread), 5000)
    return () => window.clearTimeout(timer)
  }, [viewport.mobile, preview, latestUnread, setDismissed])
  useLayoutEffect(() => {
    if (!viewport.mobile || !expanded || !partner) return
    const root = document.getElementById('root'), wasInert = root?.inert
    const previous = document.activeElement as HTMLElement | null
    if (root) root.inert = true
    if (!dock.current?.contains(document.activeElement)) dock.current?.querySelector<HTMLElement>('[aria-label="Close chat"]')?.focus({ preventScroll: true })
    return () => { if (root) root.inert = !!wasInert; if (previous?.isConnected) previous.focus({ preventScroll: true }) }
  }, [viewport.mobile, expanded, partner?.id])
  return <>
    {host && createPortal(<div className="room-toolbar chat-toolbar"><button type="button" aria-label={`Messages${summary.unreadCount ? ' · ' + summary.unreadCount + ' unread' : ''}`}
      title={error || 'Messages'} onClick={() => open(partnerId || suggested)} disabled={!others.length}>
      <MessageCircle size={17} />{summary.unreadCount > 0 && <strong className="room-count">{summary.unreadCount > 99 ? '99+' : summary.unreadCount}</strong>}
    </button></div>, host)}
    {createPortal(<><aside ref={dock} className={'chat-dock' + (expanded && partner ? ' is-expanded' : '')} aria-label="Writer chat"
      role={viewport.mobile && expanded && partner ? 'dialog' : undefined} aria-modal={viewport.mobile && expanded && partner ? true : undefined}
      onKeyDown={event => {
        if (!viewport.mobile || !expanded) return
        if (event.key === 'Escape') { event.preventDefault(); close() }
        if (event.key === 'Tab') {
          const fields = [...dock.current!.querySelectorAll<HTMLElement>('button, textarea, input, select, [tabindex="0"]')].filter(field => !field.matches(':disabled') && field.getClientRects().length)
          const first = fields[0], last = fields.at(-1)
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
        }
      }}
      style={{ '--chat-viewport-height': viewport.height + 'px', '--chat-keyboard-inset': viewport.bottom + 'px' } as React.CSSProperties}>
      {partner ? <>
        <header className="chat-header">
          <button type="button" className="chat-person" onClick={() => setExpanded(value => viewport.mobile || !value)} aria-expanded={expanded}><PresenceDot userId={partner.id} />{partner.displayName}
            {!!summary.threads.find(thread => thread.userId === partner.id)?.unreadCount && <span className="chat-unread">{summary.threads.find(thread => thread.userId === partner.id)!.unreadCount}</span>}
          </button>
          <button type="button" className="icon-button chat-toggle" aria-label={expanded ? 'Minimize chat' : 'Expand chat'} onClick={() => setExpanded(value => !value)}>{expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}</button>
          <button type="button" className="icon-button" aria-label="Close chat" onClick={close}><X size={17} /></button>
        </header>
        {expanded && <>
          {others.length > 1 && <select aria-label="Chat with" value={partner.id} onChange={e => open(e.target.value)}>{others.map(member => <option key={member.id} value={member.id}>{member.displayName}</option>)}</select>}
          <ChatConversation key={partner.id} partner={partner} onRefresh={() => void refresh()} />
        </>}
      </> : null}
    </aside>
      {preview && unread && <div key={latestUnread} className="chat-incoming" role="status">
        <button type="button" onClick={() => open(unread.userId)}><MessageCircle size={17} /><span><strong>{others.find(member => member.id === unread.userId)?.displayName || 'New message'}</strong><small>{unread.lastMessage?.text}</small></span><b>{summary.unreadCount}</b></button>
        <button type="button" className="icon-button" aria-label="Dismiss message preview" onClick={() => setDismissed(latestUnread)}><X size={15} /></button>
        <span className="chat-countdown" aria-hidden="true" />
      </div>}
    </>, document.body)}
  </>
}

function ChatConversation({ partner, onRefresh }: { partner: RoomMember; onRefresh: () => void }) {
  const { user } = useTasks(), live = useLiveRoom()
  const key = 'bramble:chat-draft:' + user.id + ':' + partner.id
  const [text, setText] = useState(() => readBrowserValue(key) || '')
  const [history, setHistory] = useState<ChatHistory | null>(null)
  const [error, setError] = useState(''), [sending, setSending] = useState(false), [loading, setLoading] = useState(false)
  const [picker, setPicker] = useState<'compose' | ChatMessage | null>(null), [reacting, setReacting] = useState(false)
  const pickerPanel = useRef<HTMLElement>(null), pickerTrigger = useRef<HTMLElement | null>(null)
  const selection = useRef({ start: text.length, end: text.length }), reactionPending = useRef(false)
  const insertedCaret = useRef<number | null>(null)
  useRefreshGuard(sending || reacting || (!!text && readBrowserValue(key) !== text), 'Waiting for your message to be safely kept before refreshing.')
  const [atBottom, setAtBottom] = useState(true), [visible, setVisible] = useState(document.visibilityState === 'visible')
  const scroll = useRef<HTMLDivElement>(null), input = useRef<HTMLTextAreaElement>(null)
  useLayoutEffect(() => {
    if (insertedCaret.current !== null) {
      input.current?.setSelectionRange(insertedCaret.current, insertedCaret.current)
      insertedCaret.current = null
    }
  }, [text, picker])
  const pages = useRef(1), generation = useRef(0), reading = useRef(false), sendPending = useRef(false)
  const keepBottom = useRef(true)
  const url = '/api/chat/' + encodeURIComponent(partner.id)
  const closePicker = (restoreFocus = true) => {
    setPicker(null)
    if (restoreFocus) pickerTrigger.current?.focus({ preventScroll: true })
  }
  useEffect(() => {
    if (!picker) return
    pickerPanel.current?.focus({ preventScroll: true })
    const outside = (event: PointerEvent) => {
      if (!pickerPanel.current?.contains(event.target as Node) && !pickerTrigger.current?.contains(event.target as Node)) setPicker(null)
    }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [picker])
  const refresh = useCallback(async () => {
    const version = ++generation.current
    try {
      const data = await roomRequest<ChatHistory>(user.id, url)
      let next = data.next
      for (let page = 1; page < pages.current && next !== null; page++) {
        const older = await roomRequest<ChatHistory>(user.id, url + '?before=' + next)
        data.messages.unshift(...older.messages); next = older.next
      }
      if (version === generation.current) { setHistory({ ...data, next }); setError('') }
      return true
    } catch (e) { if (version === generation.current) setError(e instanceof Error ? e.message : 'Messages could not be loaded.'); return false }
  }, [user.id, url])
  useEffect(() => {
    void refresh()
    const update = () => { if (document.visibilityState === 'visible') void refresh() }
    const visibility = () => { setVisible(document.visibilityState === 'visible'); update() }
    const timer = window.setInterval(update, 20000)
    window.addEventListener('bramble:chat-changed', update); document.addEventListener('visibilitychange', visibility)
    return () => { ++generation.current; clearInterval(timer); window.removeEventListener('bramble:chat-changed', update); document.removeEventListener('visibilitychange', visibility) }
  }, [refresh, live.connected])
  useLayoutEffect(() => {
    if (keepBottom.current && scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight
  }, [history?.messages.length])
  useEffect(() => {
    if (!visible || !atBottom || !history || history.readThrough >= history.latest || reading.current || document.querySelector('dialog[open]')) return
    reading.current = true
    const through = history.latest
    void roomRequest(user.id, url + '/read', 'PATCH', { through }).then(() => {
      setHistory(old => old ? { ...old, readThrough: Math.max(old.readThrough, through) } : old)
      onRefresh()
    }).catch(e => setError(e.message)).finally(() => { reading.current = false })
  }, [history, atBottom, visible, user.id, url])
  const send = async () => {
    if (sendPending.current || !text.trim()) return
    input.current?.focus({ preventScroll: true })
    sendPending.current = true; setSending(true); setError('')
    const message = text.trim(), retryKey = key + ':retry'
    let retry: { id: string; text: string } | null = null
    try { retry = JSON.parse(readBrowserValue(retryKey) || 'null') } catch { /* Ignore an invalid cached retry. */ }
    const body = retry?.text === message ? retry : { id: crypto.randomUUID(), text: message }
    writeBrowserValue(retryKey, JSON.stringify(body))
    try {
      await roomRequest(user.id, url, 'POST', body)
      setText(''); writeBrowserValue(key, ''); writeBrowserValue(retryKey, '')
      keepBottom.current = true; setAtBottom(true)
      await refresh(); onRefresh()
    } catch (e) { setError(e instanceof Error ? e.message : 'Not sent. Your message is kept here; try again.') }
    finally { sendPending.current = false; setSending(false) }
  }
  const react = async (message: ChatMessage, emoji: string | null) => {
    if (reactionPending.current) return
    reactionPending.current = true; setReacting(true); setError('')
    try {
      const data = await roomRequest<{ message: ChatMessage }>(user.id, url + '/messages/' + encodeURIComponent(message.id) + '/reaction', 'PUT', { emoji })
      setHistory(previous => previous ? { ...previous, messages: previous.messages.map(item => item.id === message.id ? data.message : item) } : previous)
      await refresh()
    } catch (e) { setError(e instanceof Error ? e.message : 'Your reaction could not be saved. Try again.') }
    finally { reactionPending.current = false; setReacting(false) }
  }
  const chooseEmoji = (emoji: string) => {
    if (picker === 'compose') {
      const { start, end } = selection.current
      const next = text.slice(0, start) + emoji + text.slice(end)
      if (next.length > 4000) { setError('Your message is full. Make a little room for the emoji.'); closePicker(); return }
      // Focus during the selection gesture so phone browsers can reopen the
      // keyboard; place the caret after React has committed the new value.
      insertedCaret.current = start + emoji.length
      input.current?.focus({ preventScroll: true })
      setText(next); writeBrowserValue(key, next); setError(''); setPicker(null)
    } else if (picker) {
      const own = history?.messages.find(message => message.id === picker.id)?.reactions?.find(reaction => reaction.userIds.includes(user.id))
      void react(picker, own?.emoji === emoji ? null : emoji); closePicker()
    }
  }
  return <>
    <div className="chat-messages" ref={scroll} aria-label={'Conversation with ' + partner.displayName} tabIndex={0} onScroll={() => {
      const element = scroll.current!
      const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 40
      keepBottom.current = bottom; setAtBottom(bottom)
    }}>
      {history?.next !== null && history && <button className="text-button chat-earlier" type="button" disabled={loading} onClick={async () => {
        setLoading(true); keepBottom.current = false
        const height = scroll.current?.scrollHeight || 0, top = scroll.current?.scrollTop || 0
        pages.current++; if (!await refresh()) pages.current--
        requestAnimationFrame(() => { if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight - height + top })
        setLoading(false)
      }}>{loading ? 'Loading…' : 'Earlier messages'}</button>}
      {!history ? <p className="chat-empty">Opening messages…</p> : !history.messages.length ? <p className="chat-empty">A little space to talk about the story. Say hello to {partner.displayName}.</p> :
        history.messages.map(message => <article key={message.id} data-message-id={message.id} className={'chat-message' + (message.fromId === user.id ? ' is-own' : '')}>
          <p>{message.text}</p><time dateTime={message.createdAt} title={new Date(message.createdAt).toLocaleString()}>{new Date(message.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time>
          {message.fromId === user.id && message.sequence === history.messages.at(-1)?.sequence && <small>{history.recipientReadThrough >= message.sequence ? 'Seen' : 'Sent'}</small>}
          <div className="chat-reactions" aria-label="Message reactions">
            {message.reactions?.map(reaction => {
              const own = reaction.userIds.includes(user.id)
              const names = reaction.userIds.map(id => id === user.id ? 'You' : partner.displayName).join(', ')
              return <button key={reaction.emoji} type="button" className="chat-reaction" aria-pressed={own} disabled={reacting}
                aria-label={reaction.emoji + ' · ' + names + (own ? ' · Remove your reaction' : ' · Add your reaction')}
                title={names + (own ? ' · Click to remove yours' : '')} onClick={() => void react(message, own ? null : reaction.emoji)}>
                <span>{reaction.emoji}</span><b>{reaction.userIds.length}</b>
              </button>
            })}
            <button type="button" className="chat-add-reaction" aria-label="React to message" title="React to message" disabled={reacting}
              aria-expanded={typeof picker === 'object' && picker?.id === message.id} aria-controls={picker && picker !== 'compose' && picker.id === message.id ? 'chat-emoji-panel' : undefined}
              onClick={event => { pickerTrigger.current = event.currentTarget; setPicker(previous => previous && previous !== 'compose' && previous.id === message.id ? null : message) }}><SmilePlus size={15} /></button>
          </div>
        </article>)}
    </div>
    {!atBottom && <button type="button" className="chat-latest" onClick={() => { keepBottom.current = true; setAtBottom(true); scroll.current?.scrollTo({ top: scroll.current.scrollHeight, behavior: 'smooth' }) }}><ArrowDown size={14} />Latest messages</button>}
    {error && <p role="alert" className="chat-error">{error}<button type="button" onClick={() => void refresh()}>Refresh</button></p>}
    {picker && <section id="chat-emoji-panel" ref={pickerPanel} className="chat-emoji-panel" aria-label={picker === 'compose' ? 'Choose an emoji' : 'Choose a reaction'} tabIndex={-1}
      onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closePicker() } }}>
      <header><span>{picker === 'compose' ? 'Add an emoji' : 'React to: ' + picker.text}</span><button type="button" className="icon-button" aria-label="Close emoji picker" onClick={() => closePicker()}><X size={17} /></button></header>
      <div className="chat-emoji-content"><EmojiPickerBoundary><Suspense fallback={<p className="chat-empty" role="status">Opening emoji…</p>}><ChatEmojiPicker onChoose={chooseEmoji} /></Suspense></EmojiPickerBoundary></div>
    </section>}
    <form className="chat-compose" onSubmit={event => { event.preventDefault(); void send() }}>
      <div className="chat-input-wrap">
      <textarea ref={input} id="chat-message-input" aria-label={'Message ' + partner.displayName} value={text} maxLength={4000} rows={2} readOnly={sending} placeholder="Write a message…"
        onChange={event => { setText(event.target.value); writeBrowserValue(key, event.target.value) }}
        onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); void send() } }} />
        <button type="button" className="chat-emoji-toggle" aria-label="Add emoji" title="Add emoji" disabled={sending} aria-expanded={picker === 'compose'} aria-controls={picker === 'compose' ? 'chat-emoji-panel' : undefined}
          onClick={event => {
            selection.current = { start: input.current?.selectionStart ?? text.length, end: input.current?.selectionEnd ?? text.length }
            pickerTrigger.current = event.currentTarget; setPicker(previous => previous === 'compose' ? null : 'compose')
          }}><Smile size={19} /></button>
      </div>
      <button type="submit" className="button button--primary" aria-label="Send message" disabled={sending || !text.trim()}><Send size={16} /></button>
      <small>{sending ? 'Sending…' : !live.connected ? 'Reconnecting · messages can still send when the server is reachable' : 'Enter to send · Shift + Enter for a new line'}</small>
    </form>
  </>
}
